import { NgModule, ErrorHandler } from '@angular/core';
import { CommonModule, DatePipe, CurrencyPipe, DecimalPipe, UpperCasePipe, TitleCasePipe, PercentPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { routes } from './shared.routes';
import { RouteNotFoundComponent } from './route-not-found/route-not-found.component';
import { LogErrorHandler } from './error-handler/error-handler';
import { LogService } from './log/log.service';
import { ConsoleLogSubscriberService } from './log/console-log-subscriber.service';
import { ErrorHandlerService } from './utils/error-handler.service';
import { HttpLogSubscriberService } from './log/http-log-subscriber.service';
import { FlashMessagesComponent } from './flash-messages/component/flash.messages.component';
import { FlashMessagesService } from './flash-messages/service/flash.messages.service';
import { PropertyLookupStore } from './lookup/property-lookup/property-lookup.store';
import { PropertyLookupHttpService } from './lookup/property-lookup/property-lookup.http.service';
import { AgencyLookupStore } from './lookup/agency-lookup/agency-lookup.store';
import { AgencyLookupHttpService } from './lookup/agency-lookup/agency-lookup.http.service';
import { BootstrapModule } from './bootstrap/bootstrap.module';
import { FormInputComponent } from './form-input/form-input.component';
import { ValidationMessageComponent } from './validation-message/validation-message.component';
import { InlineSearchComponent } from './inline-search/inline-search.component';
import { Ng2PageScrollModule } from 'ng2-page-scroll';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { EventCollisionModalComponent } from './event-collision-modal/event-collision-modal.component';
import { GenericErrorComponent } from './generic-error/generic-error.component';
import { PaginationModule } from 'ngx-bootstrap/pagination';

// Add RXJS operators so that they can be used throughout the app
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/observable/interval';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/never';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/finally';
import 'rxjs/add/operator/first';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/switch';
import { VersionComponent } from './version/version.component';
import { VersionStore } from './version/service/version.store';
import { VersionHttpService } from './version/service/version.http.service';
import { PermissionModule } from '../permission/permission.module';
import { ReservationStatusPipe } from './pipes/reservation-status.pipe';
import { PaymentStatusPipe } from './pipes/payment-status.pipe';
import { EnvironmentModule } from '../environment/environment.module';
import { EnvironmentConfiguration } from '../environment/services/environment.configuration';
import { AbsoluteValuePipe } from './pipes/absolute-value.pipe';
import { ExpirationModalComponent } from './expiration-modal/expiration-modal.component';
import { IataByDefaultModalComponent } from './iata-by-default-modal/iata-by-default-modal.component';
import { ResponseUtilService } from './utils/response-util.service';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { LocaleDateFormatPipe } from './pipes/locale-date-format.pipe';
import { PdfService } from './pdf/pdf.service';
import { PaymentTypePipe } from './pipes/payment-type.pipe';
import { TypeaheadModule } from 'ngx-bootstrap';
import { ApiDocsComponent } from '../api-docs/api-docs.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { SpinnerInterceptor } from './spinner.interceptor';
import { CacheInterceptor } from './interceptors/cache.interceptor';
import { SpinnerService } from './spinner.service';
import { ReportService } from '../event/service/report.service';
import { AbbreviateAmountPipe } from './pipes/abbreviate-amount.pipe';
import { DropdownSearchComponent } from './dropdown-search/dropdown-search.component';
import { FocusOutsideDirective } from './directives/focus-outside.directive';
import { HighlightPipe } from './pipes/highlight.pipe';
import { PaginationComponent } from './pagination/pagination.component';
import { PaymentCalculationComponent } from './components/payment-calculation/payment-calculation.component';
import { UppercaseDirective } from './directives/uppercase.directive';
import { MasterBillPipe } from './pipes/master-bill.pipe';
import { MultiAgencyService } from './multi-agency/multi-agency.service';
import { MatchingPropertyService } from './matching-property/matching-property.service';
import { PaginationSortComponent } from './pagination-sort/pagination-sort.component';

@NgModule({
  imports: [
    CommonModule,
    BootstrapModule,
    TranslateModule,
    FormsModule,
    PermissionModule,
    EnvironmentModule,
    RouterModule.forChild(routes),
    Ng2PageScrollModule.forRoot(),
    TypeaheadModule.forRoot(),
    PaginationModule.forRoot(),
    ReactiveFormsModule
  ],
  exports: [
    CommonModule,
    BootstrapModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    InlineSearchComponent,
    FlashMessagesComponent,
    FormInputComponent,
    ValidationMessageComponent,
    Ng2PageScrollModule,
    ConfirmationModalComponent,
    EventCollisionModalComponent,
    ExpirationModalComponent,
    IataByDefaultModalComponent,
    VersionComponent,
    PermissionModule,
    EnvironmentModule,
    PaymentStatusPipe,
    MasterBillPipe,
    ReservationStatusPipe,
    AbsoluteValuePipe,
    DateFormatPipe,
    LocaleDateFormatPipe,
    PaymentTypePipe,
    TypeaheadModule,
    ApiDocsComponent,
    AbbreviateAmountPipe,
    PaginationModule,
    DropdownSearchComponent,
    PaginationComponent,
    PaginationSortComponent,
    PaymentCalculationComponent,
    UppercaseDirective,
  ],
  declarations: [
    ConfirmationModalComponent,
    EventCollisionModalComponent,
    ExpirationModalComponent,
    IataByDefaultModalComponent,
    RouteNotFoundComponent,
    InlineSearchComponent,
    FlashMessagesComponent,
    FormInputComponent,
    ValidationMessageComponent,
    VersionComponent,
    GenericErrorComponent,
    PaymentStatusPipe,
    MasterBillPipe,
    ReservationStatusPipe,
    AbsoluteValuePipe,
    DateFormatPipe,
    LocaleDateFormatPipe,
    PaymentTypePipe,
    ApiDocsComponent,
    AbbreviateAmountPipe,
    DropdownSearchComponent,
    FocusOutsideDirective,
    HighlightPipe,
    PaginationComponent,
    PaginationSortComponent,
    PaymentCalculationComponent,
    UppercaseDirective,
  ],
  providers: [
    AgencyLookupHttpService,
    AgencyLookupStore,
    ErrorHandlerService,
    LogService,
    ConsoleLogSubscriberService,
    FlashMessagesService,
    HttpLogSubscriberService,
    PropertyLookupHttpService,
    PropertyLookupStore,
    VersionStore,
    VersionHttpService,
    SpinnerService,
    { provide: ErrorHandler, useClass: LogErrorHandler },
    ResponseUtilService,
    PdfService,
    MultiAgencyService,
    MatchingPropertyService,
    DatePipe,
    ReservationStatusPipe,
    CurrencyPipe,
    DecimalPipe,
    PercentPipe,
    UpperCasePipe,
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    ReportService,
    AbbreviateAmountPipe,
    HighlightPipe,
    TitleCasePipe,
  ]
})
export class SharedModule {
  constructor(consoleLogSubscriber: ConsoleLogSubscriberService,
              httpLogSubscriber: HttpLogSubscriberService,
              environment: EnvironmentConfiguration) {
    if (environment.logServices.consoleLogSubscriber) {
      consoleLogSubscriber.init(environment.logServices.consoleLogSubscriber.logLevel);
    }

    if (environment.logServices.httpLogSubscriber) {
      httpLogSubscriber.init(environment.logServices.httpLogSubscriber.logLevel);
    }
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/shared/shared.module.ts